import type { DeepPartial, ChartOptions } from "lightweight-charts";

export const CHART_HEIGHT_PX = 192;

/**
 * Chart colors are theme-aware. `lightweight-charts` is a vanilla JS library
 * and cannot read CSS variables directly, so we resolve them at runtime via
 * getComputedStyle on the document root. Consumers should call
 * `getChartColors()` and `getCommonChartOptions()` inside their useEffect
 * with the active theme as a dependency, so the chart re-initialises on
 * theme switch.
 *
 * Fallback values match the default Executive (WickScope) palette so SSR /
 * first paint don't flash.
 */

interface ChartColors {
  positive: string;
  negative: string;
  line: string;
  background: string;
  textColor: string;
  gridLine: string;
}

const FALLBACK_COLORS: ChartColors = {
  positive: "#2f8a5d",
  negative: "#c04a33",
  line: "#c8632a",
  background: "#fbf9f4",
  textColor: "#15110d",
  gridLine: "#e6e0d3",
};

function readCssVar(name: string, fallback: string): string {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return fallback;
  }
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return raw.length > 0 ? raw : fallback;
}

export function getChartColors(): ChartColors {
  return {
    positive: readCssVar("--color-success", FALLBACK_COLORS.positive),
    negative: readCssVar("--color-danger", FALLBACK_COLORS.negative),
    line: readCssVar("--color-primary", FALLBACK_COLORS.line),
    background: readCssVar(
      "--color-surface-container",
      FALLBACK_COLORS.background,
    ),
    textColor: readCssVar("--color-on-surface", FALLBACK_COLORS.textColor),
    gridLine: readCssVar(
      "--color-outline-variant",
      FALLBACK_COLORS.gridLine,
    ),
  };
}

export function getCommonChartOptions(): DeepPartial<ChartOptions> {
  const c = getChartColors();
  return {
    layout: { background: { color: c.background }, textColor: c.textColor },
    grid: { vertLines: { visible: false }, horzLines: { color: c.gridLine } },
    timeScale: { borderVisible: false },
    rightPriceScale: { borderVisible: false },
  };
}

/**
 * Static exports preserved for components that haven't been migrated yet.
 * They reflect the Editorial palette; do not use these in new code — call
 * `getChartColors()` instead so charts react to theme changes.
 *
 * @deprecated Use `getChartColors()` for theme-reactive rendering.
 */
export const POSITIVE_COLOR = FALLBACK_COLORS.positive;
export const NEGATIVE_COLOR = FALLBACK_COLORS.negative;
export const LINE_COLOR = FALLBACK_COLORS.line;

/**
 * @deprecated Use `getCommonChartOptions()` for theme-reactive rendering.
 */
export const COMMON_CHART_OPTIONS: DeepPartial<ChartOptions> = {
  layout: {
    background: { color: FALLBACK_COLORS.background },
    textColor: FALLBACK_COLORS.textColor,
  },
  grid: {
    vertLines: { visible: false },
    horzLines: { color: FALLBACK_COLORS.gridLine },
  },
  timeScale: { borderVisible: false },
  rightPriceScale: { borderVisible: false },
};
